import c from 'classnames';
import { Suspense, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useMatch, useNavigate } from 'react-router-dom';

import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { useNavKitId } from 'hooks/useNavKitId';
import { getHomePageTinyAccountId } from 'store/home';
import { getKitById, getLatestKitForTinyAccountId } from 'store/kits';
import TinyAccount from 'types/TinyAccount';

import { BackButton } from '@repo/ui';
import { Brand, Logo, TinyAccountSelector } from 'components';
import { useSidebarItems } from 'components/Sidebar/useSidebarItems';
import SidebarItem from './SidebarItem';
import { TinyPlusMemberBadge, TinyPlusUpgradeButton } from './TinyPlusNav';

import styles from './Sidebar.module.css';

export const Sidebar = () => {
  const navigate = useNavigate();

  const { currentKitId } = useCurrentKitAndTinyAccount();
  const homeScreenMatch = useMatch('/');
  const resultsMatch = useMatch<'kitId', string>('/results/:kitId');
  const actionPlanMatch = useMatch<'kitId', string>('/action-plan/:kitId');
  const homePageTinyAccountId = useSelector(getHomePageTinyAccountId);
  const mostRecentKitIdFromHomePage = useSelector(
    getLatestKitForTinyAccountId(homePageTinyAccountId ?? null),
  )?.id;
  const kitIdParam = homeScreenMatch
    ? (mostRecentKitIdFromHomePage ?? '')
    : resultsMatch?.params.kitId ||
      actionPlanMatch?.params.kitId ||
      currentKitId ||
      mostRecentKitIdFromHomePage ||
      '';

  const navKitId = useNavKitId();
  const kit = useSelector(getKitById(kitIdParam));
  const tinyAccountId = kit?.tinyaccount_id;
  const tinyAccountSelected = useCallback(
    async (tinyAccount?: Partial<TinyAccount>) => {
      if (tinyAccount?.id) {
        navigate(`/`, { state: { tinyAccountId: tinyAccount.id } });
      }
    },
    [navigate],
  );

  const isResultsPage = useMatch('/results/:kitId/*');
  const isActionPlanPage = useMatch('/action-plan/:kitId/*');

  const isTopLevelResults = useMatch({ path: '/results/:kitId', end: true });
  const isTopLevelActionPlan = useMatch({
    path: '/action-plan/:kitId',
    end: true,
  });

  const showBackButton =
    (isResultsPage || isActionPlanPage) &&
    !(isTopLevelResults || isTopLevelActionPlan);

  const goBack = () => navigate(-1);

  const sidebarItems = useSidebarItems({ kitId: navKitId });

  return (
    <div className={styles.sideBarWrapper}>
      <div className={c(styles.sidebarContainer)}>
        <div className={styles.logoContainer}>
          <div className={styles.navLogo}>
            <Suspense fallback={<Logo />}>
              <Brand href='/' />
            </Suspense>
          </div>
          <TinyPlusMemberBadge />
        </div>

        <div className={styles.selectorContainer}>
          <TinyAccountSelector
            onSelection={tinyAccountSelected}
            currentTinyAccountId={tinyAccountId ?? homePageTinyAccountId}
            withProfile
          />
        </div>
        <div className={styles.itemsContainer}>
          <div>
            {sidebarItems
              .filter(item => !item.bottom)
              .map((item, index) => (
                <SidebarItem
                  key={index}
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  external={item.external}
                  state={{ tinyAccountId }}
                  customClassName={item.customClassName}
                  onClick={item.onClick}
                  showDot={item.showDot}
                />
              ))}
          </div>
          {/* bottom section of nav items*/}
          <div>
            <TinyPlusUpgradeButton />
            {sidebarItems
              .filter(item => item.bottom)
              .map((item, index) => (
                <SidebarItem
                  key={index}
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  external={item.external}
                  state={{ tinyAccountId }}
                  customClassName={item.customClassName}
                  onClick={item.onClick}
                  showDot={item.showDot}
                />
              ))}
          </div>
        </div>
      </div>
      {showBackButton && <BackButton variant='button' goBack={goBack} />}
    </div>
  );
};
